export interface DocSectionContent {
	id: string;
	title: string;
	summary: string;
	body: string;
}

export const DOC_SECTIONS: readonly DocSectionContent[] = [
	{
		id: "getting-started",
		title: "Getting started",
		summary: "Three things to do before a shopper can see an offer.",
		body: `1. Set up your theme once. See Storefront setup below.
2. Build a campaign: when it runs, what a shopper has to spend, and what they get.
3. Publish it.

Saving and publishing are separate. Nothing reaches shoppers until you publish.`,
	},
	{
		id: "campaign-types",
		title: "Campaign types",
		summary: "Simple free gift and tiered free gifts, and when to use each.",
		body: `### Simple free gift

One gift behind an optional minimum spend.

Leave the minimum at 0 and the gift is offered on every order. Set it to 50 and the gift appears once the cart reaches 50.

Use this when there is one thing you want to give away.

### Tiered free gifts

Several spend thresholds, each unlocking its own gift. A 200 cart can clear the 30, 70 and 150 tiers at once.

**Stacking** decides what happens when more than one tier clears. \`All cleared tiers\` hands over every gift. \`Highest tier only\` hands over the last one.

Use this when you want a ladder that pulls order value up.

### What counts toward a threshold

The cart total after discounts, not counting anything that is already a gift. A gift can never pay for the next one.`,
	},
	{
		id: "build-a-campaign",
		title: "Building a campaign",
		summary: "The five steps of the builder and what each one decides.",
		body: `Each step saves on its own, so you can leave and come back.

1. **Details.** An internal name and the status. Shoppers never see the name.
2. **Schedule.** A start and end date.
3. **Rules.** Stacking, round up, and which other discounts this one combines with.
4. **Tiers and gifts.** The thresholds and what each one hands over.
5. **Content.** Popups and the floating ticker.

### Round up

Round up forgives a small shortfall. Enable it and set 0.05, and a cart at 69.96 clears a 70 tier. Leave it off and thresholds are exact.

### Combines with

Whether the gift discount stacks with other discounts on the same order. Turn order discounts off and a shopper using a promo code loses the gift. Turn them on and both apply.

### Statuses

A campaign is \`draft\`, \`active\` or \`ended\`. Only an active campaign inside its date window is shown to shoppers.`,
	},
	{
		id: "subscription-upsell",
		title: "Subscription upsell",
		summary: "Ask shoppers to move a cart item onto a subscription, before they check out.",
		body: `A shopper adds something you also sell on a subscription, but adds it as a one time purchase. This offer notices and asks whether they would rather subscribe, switching the cart line if they say yes.

It is the only offer type that interrupts rather than rewards, so it is deliberately quiet. The prompt appears when the shopper opens their cart, never while they are still adding to it, and only once per product per visit. Turning it down is remembered for the rest of the visit.

### Pin a plan, or let them choose

**Pin one plan** and the prompt is a single yes or no. This converts best and is the right choice when there is an obvious cadence.

**Let the shopper choose** and they pick from every plan the product is sold on. Worth it only when cadence genuinely varies between customers, because it turns a yes into a decision.

### Only when

Conditions gate the prompt on the state of the whole cart, so an upsell can be kept for the carts worth interrupting. All of them have to hold.

You can require a quantity of the item itself, require or forbid something else in the cart, or set a floor or ceiling on the cart total or unit count. **The cart does not contain** is the one that earns its place: it keeps the offer away from someone already buying a subscription box or a gift set.

Add no conditions and the offer shows on any cart holding a matching product.

### Extra discounting

The plan already carries whatever saving you configured in Shopify, and that applies on its own once the line switches. An extra discount here comes off on top, and is pinned to the plan being pitched so it cannot land on a subscription the shopper already had.

Leave it at none and the plan's own saving is the whole offer, in which case publishing registers no discount with Shopify at all.

### What it does not do

It never adds a product. It moves a line the shopper chose onto a plan, and it can move it back.`,
	},
	{
		id: "gifts",
		title: "Gifts",
		summary: "How one gift can be a choice, a pair of items, or a single fixed product.",
		body: `A gift is what one tier hands over. Put one product in it and that is what the shopper receives. Put in several and you decide whether they get all of them or pick one.

### One product or several

With a second product added, a control appears above the list: **Shoppers get all of these**, for a gift that is two items together, or **one of these, their pick**, for a choice such as a tote or a cap.

With a single product there is nothing to decide, so nothing is asked.

### Variants

A product can be pinned to one variant, in which case there is nothing to choose. Or the variant can be left open and the shopper picks their size.

### Add it to the cart for them

The gift is added the moment the tier clears, with no prompt.

This is only offered when nothing in the gift needs a decision. Several products can be added at once, so long as each is pinned to a variant and the shopper is not being asked to pick between them. Anything they have to choose opens the picker instead, so the setting does not appear.

If the shopper removes it, it stays gone for the rest of their visit. They can add it again from the cart at any time.

### Value you advertise

What the gift is worth. It shows as a struck through price next to the word Free.

### Availability threshold

Stop offering a gift once giving it away would push stock below this number. Set 50 to hold 50 units back for paying customers. Leave it at 0 to give it away until stock runs out.`,
	},
	{
		id: "storefront-setup",
		title: "Storefront setup",
		summary: "Two changes in your theme, done once.",
		body: `Both live in the Shopify theme editor.

### 1. Turn on the app embed

Open **App embeds** and enable **AOVtools Content**.

Without this, nothing appears anywhere on your store.

### 2. Place the cart progress block

Add the **AOVtools gift progress** block to your cart page. It shows the progress bar and the gift cards.

If your cart drawer accepts app blocks, add it there too. Open the drawer in the editor and look for **Add block** then **Apps**.

Some themes do not allow blocks in the cart drawer. In that case the widget places itself just above your cart items. To move it, open the **AOVtools Content** app embed and fill in **Mini cart placement**.`,
	},
	{
		id: "promotional-content",
		title: "Promotional content",
		summary: "Popups and the floating ticker.",
		body: `Content is added from the **Content** step of a campaign. Use **Add content** and pick a type. A campaign can hold several popups but only one floating ticker.

### Popup

A full screen image, shown once a day per shopper shortly after the page loads. Clicking anywhere on it closes it.

Upload a desktop image and, if you want one, a separate mobile image. Alt text is read aloud by screen readers and shown if the image fails to load, so describe the offer rather than the artwork.

A popup runs for the whole campaign by default. Give it a date window instead and you can run a sequence, so Happening now becomes Ends tomorrow without editing anything mid sale.

### Floating ticker

A small badge in the corner of every page. It counts down to the end of the campaign, names the next gift, and says how much more the shopper needs to spend.

Once a gift is unlocked and still unclaimed, the ticker switches to a claim prompt and opens the picker when tapped.

Set the headline, light or dark, which corner it sits in, and whether the progress bar shows. The editor previews it as you type.

A campaign with no end date has no countdown, so the ticker shows the next gift and progress only.`,
	},
	{
		id: "publishing",
		title: "Publishing",
		summary: "How changes go live, and how to end a campaign.",
		body: `Everything you change is saved as you work. None of it reaches shoppers until you press **Publish**.

A campaign that has been published and edited since shows an **Unpublished changes** badge, both in the campaign list and on the campaign itself, with a prompt to publish again.

Allow a few minutes after publishing for the change to appear to shoppers who are already browsing.

### Ending a campaign

Set the status to \`ended\` and publish. The offer stops being shown and the discount is removed.

Gifts already in a shopper's cart stay there. Nothing is taken away from someone part way through checking out.`,
	},
];
